import React from 'react';
import { useIntl } from 'react-intl';
import classNames, { Argument } from 'classnames';

import { useSelector } from '../../hooks';
import useMediaMetrics from 'hooks/useMediaMetrics';
import { isLowPerfMode } from 'modules/app/selectors';

import { AbstractMedia } from 'types/AbstractMedia';
import { safePlay } from 'utils/mediaUtils';

import Icon from './Icon';
import Button, { ButtonType } from './Button';

interface VideoProps extends React.DetailedHTMLProps<Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'className'>, HTMLVideoElement> {
  media: AbstractMedia;
  autoplay?: boolean;
  thumbnail?: boolean;
  className?: Argument;
  disableAutoPause?: boolean;
  'data-testid'?: string;
}

const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);

const Video: React.FC<VideoProps> = ({
  media, autoplay, thumbnail, 'data-testid': testId, onSeeking, className, disableAutoPause, ...rest
}) => {
  const intl = useIntl();
  const ref = React.useRef<HTMLVideoElement>(null);

  const mediaMetrics = useMediaMetrics(media);
  const lowPerfMode = useSelector(isLowPerfMode);

  const isBlobUrl = media.path.startsWith('blob:');
  const hasMediaThumbnail = media.thumbnailPath;
  const shouldLoadVideo = autoplay || !lowPerfMode || !thumbnail;

  const playVideo = React.useCallback(() => {
    if (isIOSSafari) {
      ref.current.playsInline = true;
      ref.current.autoplay = true;
    }
    safePlay(ref.current);
  }, [ref]);

  // Pause video when it's not visible
  React.useLayoutEffect(() => {
    if (ref.current && shouldLoadVideo && !disableAutoPause) {
      if (thumbnail && isIOSSafari && isBlobUrl) {
        // Trick to load preview on IOS safari with blob url
        ref.current.autoplay = true;
        ref.current.pause();
        ref.current.load();
        ref.current.autoplay = false;
      }

      if (thumbnail && !ref.current.paused) {
        ref.current.pause();
        ref.current.currentTime = 0;
      }

      const observer = new IntersectionObserver((entries) => {
        if (entries.every(({ isIntersecting }) => !isIntersecting)) {
          ref.current.pause();
          ref.current.currentTime = 0;
        } else if (ref.current.paused && !thumbnail) {
          playVideo();
        }
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      });

      observer.observe(ref.current as HTMLVideoElement);

      return () => observer.disconnect();
    }

    return undefined;
  }, [playVideo, isBlobUrl, thumbnail, shouldLoadVideo]);

  return (
    <>
      {thumbnail && !autoplay && (
        <Button
          testId="carousel-play"
          type={ButtonType.WHITE}
          // eslint-disable-next-line max-len
          className="rounded-button size-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 hover:bg-white/90 drop-shadow-xl active:!bg-white !p-1 z-20 cursor-pointer play-button flex justify-center items-center"
          aria-label={intl.formatMessage({ id: 'accessibility.button.play', defaultMessage: 'Play the video' })}
        >
          <Icon className="fb-icon--play text-2xl kiosk:text-4xl text-default" />
        </Button>
      )}
      {!shouldLoadVideo && hasMediaThumbnail && (
        <div
          style={{ backgroundImage: `url("${media.thumbnailPath}")` }}
          className={classNames('relative bg-contain bg-origin-padding bg-center bg-no-repeat h-full w-full bg-card-bg', className)}
        />
      )}
      {shouldLoadVideo && (
        <video
          ref={ref}
          preload="auto"
          data-testid={testId}
          autoPlay={autoplay}
          onSeeking={onSeeking}
          disableRemotePlayback
          playsInline={autoplay}
          disablePictureInPicture
          muted={autoplay && thumbnail}
          controls={autoplay || !thumbnail}
          controlsList="nofullscreen nodownload noremoteplayback noplaybackrate nopip"
          className={classNames('max-h-full w-full', thumbnail ? 'bg-black' : 'bg-default')}
          aria-label={intl.formatMessage({ id: 'accessibility.button.play', defaultMessage: 'Play the video' })}
          {...mediaMetrics}
          {...rest}
        >
          <source src={isBlobUrl ? media.path : `${media.path}#t=0.001`} type={media.contentType} />
          <track kind="captions" />
        </video>
      )}
    </>
  );
};

export default Video;
